import { Injectable } from '@angular/core';
import { HttpService } from 'projects/cms/src/app/core/http/http.service';
import { environment } from 'projects/cms/src/environments/environment';
import { Logger } from 'projects/shared/src/lib/shared/core/logger.service';
import {
  ApplicationUser,
  ApplicationUserCreateOptions,
  ApplicationUserUpdateOptions,
  ResetPasswordBody,
} from 'projects/shared/src/lib/shared/models/classes/users/ApplicationUser';
import { Response } from 'projects/shared/src/lib/shared/models/local/Response';
import { EntityService } from 'projects/shared/src/lib/shared/services/entity.service';
import { I18nService } from 'projects/shared/src/lib/shared/services/local/i18n.service';
import { map, Observable } from 'rxjs';

const log = new Logger('Users Service');

@Injectable()
export class UsersService extends EntityService<ApplicationUser, ApplicationUserCreateOptions, ApplicationUserUpdateOptions> {

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    protected httpService: HttpService,
    protected i18nService: I18nService
  ) {
    super('users', 'User', httpService, i18nService);
  }

  update(id: string, entity: ApplicationUserUpdateOptions): Observable<ApplicationUser> {
    return super.update(id, entity);
  }

  getCurrentUser(): Observable<ApplicationUser> {
    return this.httpService.disableApiPrefix().get<any>(`${environment.idpServerUrl}api/v1/users/me`).pipe(
      map((result: any) => {
        return result.data;
      })
    );
  }

  resetPassword(userId: string, resetPasswordDto: ResetPasswordBody) {

    return this.httpService.patch<any>(
      `users/${userId}/password`,
      resetPasswordDto
    ).pipe(
      map((result: Response<boolean>) => {
        return result.code.statusCode === 200;
      })
    );
  }

  sendPasswordResetEmail(email: string) {

    return this.httpService
      .post<any>(
        `users/password-reset`, email
      ).pipe(
        map((result: Response<boolean>) => {
          return result.code.statusCode === 200;
        })
      );
  }

  confirmEmail(userId: string, token: string): Observable<ApplicationUser> {
    return this.httpService
      .disableAccessToken()
      .post<any>(
        `users/${userId}/confirm-email`,
        { token }
      ).pipe(
        map((result: Response<ApplicationUser>) => {
          return result.data;
        })
      );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
    return this.httpService
      .patch<Response<boolean>>(`users/me/password`, {
        currentPassword,
        password: newPassword
      })
      .pipe(
        map((result: Response<boolean>) => {
          return result.code.statusCode === 200;
        })
      );
  }

  changePermissions(userId: string, permissions: string[], useCaseIds: string[]): Observable<boolean> {
    return this.httpService
      .put<Response<boolean>>(`users/${userId}/permissions`, { permissions, useCaseIds })
      .pipe(
        map((result: Response<boolean>) => {
          return result.data;
        })
      );
  }

  changeRoles(userId: string, userRole: string): Observable<boolean> {
    return this.httpService
      .patch<Response<boolean>>(`users/${userId}/roles`, { role: userRole })
      .pipe(
        map((result: Response<boolean>) => {
          return result.code.statusCode === 200;
        })
      );
  }

  deleteManyByIds(ids: string[]): Observable<boolean> {
    const filter = ids.map(id => `id=${id}&`).join('');
    return this.httpService.delete<Response<boolean>>(`users?${filter}`).pipe(
      map((result: Response<boolean>) => {
        return result.code.statusCode === 200;
      })
    );
  }
}
